import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { previews } from '../data/previews';
import { optimizedImage, optimizedSrcSet } from '../utils/image';
const PAGE_WIDTHS = [360, 540, 720];
const arrowClass = 'flex h-12 w-12 items-center justify-center rounded-full bg-accent text-white shadow-md shadow-accent/20 transition-[opacity,background-color,transform] duration-150 ease-out hover:bg-accent-hover active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:pointer-events-none disabled:opacity-30';
const PAGE_SIZES = '(min-width: 1024px) 300px, (min-width: 640px) 45vw, 80vw';

/**
 * Carrusel deslizable: en móvil se desliza con el dedo (scroll-snap nativo,
 * sin librerías); en ordenador hay flechas. Los puntos indican la página visible.
 */
export function InsideSection() {
  const trackRef = useRef<HTMLUListElement>(null);
  const slideRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [edges, setEdges] = useState({
    atStart: true,
    atEnd: false
  });
  const getStep = (): number => {
    const first = slideRefs.current[0];
    const second = slideRefs.current[1];
    return first && second ? second.offsetLeft - first.offsetLeft : 0;
  };
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    // La página activa es la que está alineada al inicio del carril.
    const update = () => {
      frame = 0;
      const step = getStep();
      if (!step) return;
      const atStart = track.scrollLeft <= 4;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      const index = atEnd ? previews.length - 1 : Math.round(track.scrollLeft / step);
      setActiveIndex(Math.min(previews.length - 1, Math.max(0, index)));
      setEdges({
        atStart,
        atEnd
      });
    };
    update();
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  const goTo = (index: number) => {
    trackRef.current?.scrollTo({
      left: index * getStep(),
      behavior: 'smooth'
    });
  };
  const scrollByOne = (direction: 1 | -1) => {
    trackRef.current?.scrollBy({
      left: direction * getStep(),
      behavior: 'smooth'
    });
  };
  const isFirst = edges.atStart;
  const isLast = edges.atEnd;
  return <section id="dentro" className="defer-paint w-full scroll-mt-6 overflow-hidden bg-card py-12 sm:py-14" aria-labelledby="inside-heading" data-id="element-118" data-mp="48prowiftw4d">
      <div className="mx-auto max-w-6xl px-6" data-id="element-119" data-mp="32vk8oyfqhwn1">
        <SectionHeading eyebrow="Anteprima" title={<span id="inside-heading" data-id="element-121" data-mp="37uwv5i2p11p3">Guarda dentro Yoga Kit Pro™</span>} lead="Pagine reali dei volumi: tempi, posizioni, varianti e frasi pronte da dire in sala. Usa le frecce o scorri per vederle." data-id="element-120" data-mp="37vzjpjcjms1u" />

        <div className="relative mt-10" data-id="element-122" data-mp="2vex26rj6bp9v">
          <ul ref={trackRef} className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-6 px-6 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Pagine di esempio dei volumi" data-id="element-123" data-mp="2qesplcldzeox">
            {previews.map((preview, index) => <li key={preview.id} ref={el => {
            slideRefs.current[index] = el;
          }} data-index={index} className="w-[80%] shrink-0 snap-start sm:w-[45%] lg:w-[calc((100%-2rem)/3)]" aria-label={`${index + 1} di ${previews.length}`} data-id="element-124" data-mp="2hfxilyaiq0kk" data-mpi={index}>
                <figure data-id="element-125" data-mp="22pzybtfplv0n">
                  <div className="aspect-[1688/2100] overflow-hidden rounded-2xl border border-line bg-canvas shadow-lg shadow-accent/10" data-id="element-126" data-mp="22qsww74ile7p">
                    <img src={optimizedImage(preview.image, 540)} srcSet={optimizedSrcSet(preview.image, PAGE_WIDTHS)} sizes={PAGE_SIZES} alt={preview.alt} width={1688} height={2184} loading="lazy" decoding="async" draggable={false} className="h-full w-full select-none object-cover object-center" data-id="element-127" data-mp="1xs7vw5hfuqi7" />
                  </div>
                  <figcaption className="mt-3 px-1" data-id="element-128" data-mp="29nduqxo266mu">
                    <span className="block font-sans text-[10px] font-semibold uppercase tracking-[0.14em] text-accent" data-id="element-129" data-mp="29mvrhh2zas5f">
                      {preview.volume}
                    </span>
                    <span className="mt-1 block font-serif text-lg font-bold leading-snug text-ink" data-id="element-130" data-mp="1zo6h09dew9z7">
                      {preview.title}
                    </span>
                  </figcaption>
                </figure>
              </li>)}
          </ul>
        </div>

        {/* Controles visibles en todas las pantallas: flecha · puntos · flecha. */}
        <div className="mt-6 flex items-center justify-center gap-4" data-id="element-131" data-mp="3l2keu9k59t6r">
          <button type="button" onClick={() => scrollByOne(-1)} disabled={isFirst} aria-label="Pagina precedente" className={arrowClass} data-id="element-132" data-mp="3l06ky417an0s">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-133" data-mp="1dg148wdsnmfv" />
          </button>

          <div className="flex items-center" data-id="element-134" data-mp="19f7gc30cq7mz">
            {previews.map((preview, index) => <button key={preview.id} type="button" onClick={() => goTo(index)} aria-label={`Vai alla pagina ${index + 1}`} aria-current={index === activeIndex} className="flex h-8 min-w-[2rem] items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent" data-id="element-135" data-mp="19d4iiwsyja7p" data-mpi={index}>
                <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${index === activeIndex ? 'w-6 bg-accent' : 'w-2 bg-line'}`} data-id="element-136" data-mp="1s5fcu29jpzu7" />
              </button>)}
          </div>

          <button type="button" onClick={() => scrollByOne(1)} disabled={isLast} aria-label="Pagina successiva" className={arrowClass} data-id="element-137" data-mp="215lrhrgzd5uy">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-138" data-mp="brp8cueil9k1" />
          </button>
        </div>
      </div>
    </section>;
}